/** @jsxImportSource @emotion/react */
import { colors } from "../../../../components/colors";
import * as yup from "yup";
import { css } from "@emotion/react";
import * as authSelectors from "../../../auth/authSelectors";
import {
  ButtonAction,
  ButtonInternalLink,
  ButtonPrimary,
  ButtonPrimaryForwardedRef,
} from "../../../../components/NewButtons";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { AutoCampaign, AutoCampaignFormType } from "../models/AutoCampaign";
import { StyledText } from "../../../../assets/typography/typography";
import { TriggerConfigField } from "./TriggerConfigField";
import { useGetProductGroups } from "../../product-groups/service/product-groups-api";
import { SpinnerBlock } from "../../../common/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SmallBadge } from "../../../../components/SmallBadge";
import { LabelledRadioGroupInput } from "../../../../components/forms/inputs/LabelledRadioGroupInput";
import { TextInput } from "../../../../components/forms/inputs/TextInput";
import {
  PriceInput,
  transformPriceInput,
} from "../../../../components/forms/inputs/PriceInput";
import { FormRow } from "../../../common/hook-form";
import { useCreateCampaign, useUpdateCampaign } from "../service/campaign-api";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../../error-handler";
import { ActionSpinner } from "../../../../components/ActionSpinner";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { SelectCountryInput } from "../../../../components/forms/inputs/SelectCountryInput";
import { SelectCurrencyInput } from "../../../../components/forms/inputs/SelectCurrencyInput";
import {
  getCountrySpecificValues,
  transformPriceFromAPI,
} from "../../../../util";
import { ErrorMessage } from "@hookform/error-message";
import { DatePickerInput } from "../../../../components/forms/inputs/DatePickerInput";
import { Disclosure } from "../../../../components/Disclosure";
import { useGetCountries } from "../../../../service/api";
import { SwitchInput } from "../../../../components/forms/inputs/SwitchInput";

export const buttonWrapperStyle = css`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: ${colors.white};
  display: flex;
  flex-direction: row;
  border-radius: 0px 0px 8px 8px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.greyBorder};
  gap: 8px;
  width: 100%;
  align-items: center;
  height: fit-content;
  margin-top: auto;
  justify-content: end;
`;

export const formStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
`;

export const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 24px;
  padding-bottom: 24px;
  width: 600px;
`;

export const bodySectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const bodyHeaderStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const discountTypeWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

type Props = {
  campaign?: AutoCampaign;
};

const schema = yup.object({
  activeFrom: yup.string().required("From date is required"),
  activeTo: yup.string().required("To date is required"),
  discountType: yup.string().required("Discount type is required"),
  discountValue: yup.string().when("discountType", {
    is: (discountType: string) => discountType === "percentage_discount",
    then: (schema) =>
      schema
        .required("Discount percentage is required")
        .test(
          "discountValue",
          "Discount percentage must be between 0 and 100",
          (value) => {
            if (!value) {
              return false;
            }

            try {
              const number = Number(value);
              if (number > 100) {
                return false;
              }

              if (number < 0) {
                return false;
              }
            } catch (error) {
              return false;
            }

            return true;
          }
        ),
    otherwise: (schema) =>
      schema.test(
        "discountValue",
        "Discount value must be above 0",
        (value, ctx) => {
          if (!value) {
            return false;
          }
          const currency = ctx.parent.currency;
          const parsedValue = transformPriceInput(value, currency);
          return parsedValue > 0;
        }
      ),
  }),
  name: yup.string().required("Admin name is required"),
  title: yup.string().required("Title is required"),
  countryAlpha2Code: yup.string().required("Country is required"),
  productGroupConfigurations: yup
    .array()
    .of(
      yup.object({
        triggerCount: yup
          .number()
          .typeError("Number of products is required")
          .min(1, "Must be at least 1"),
        productGroupIds: yup
          .array()
          .of(yup.string())
          .min(1, "At least one product group is required"),
      })
    )
    .min(1, "Create at least one condition"),
});

export const MutateCampaignForm = ({ campaign }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { countryCode, currency } = getCountrySpecificValues();
  const navigate = useNavigate();
  const methods = useForm<AutoCampaignFormType>({
    defaultValues: {
      activeFrom: campaign?.activeFrom || "",
      activeTo: campaign?.activeTo || "",
      countryAlpha2Code: campaign?.countryAlpha2Code || countryCode,
      currency: campaign?.currency || currency,
      discountType: campaign?.discountType || "fixed_price",
      enabled: campaign ? campaign.enabled : true,
      discountValue: campaign
        ? campaign?.discountType === "fixed_price"
          ? transformPriceFromAPI(campaign?.discountValue, campaign?.currency)
          : campaign?.discountValue.toString()
        : "0",
      name: campaign?.name || "",
      title: campaign?.title || "",
      productGroupConfigurations:
        campaign?.productGroupConfigurations.map((pg) => ({
          triggerCount: pg.triggerCount,
          productGroupIds: pg.productGroups.map((pg) => pg.id),
        })) || [],
    },
    resolver: yupResolver(schema),
  });
  const {
    data: productGroups,
    isLoading: isLoadingProductGroups,
  } = useGetProductGroups(organizationKeyName);

  const { mutateAsync: createCampaign } = useCreateCampaign();
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();
  const { mutateAsync: updateCampaign } = useUpdateCampaign(campaign?.id);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "productGroupConfigurations",
  });

  const onSubmit = async (data: AutoCampaignFormType) => {
    try {
      const discountValue =
        data.discountType === "fixed_price"
          ? transformPriceInput(data.discountValue, data.currency)
          : Number(data.discountValue);
      if (campaign) {
        await updateCampaign({
          data: {
            ...data,
            id: campaign.id,
            discountValue,
          },
          organizationKeyName: organizationKeyName,
        });
      } else {
        await createCampaign({
          data: {
            ...data,
            discountValue,
          },
          organizationKeyName: organizationKeyName,
        });
      }
      toastr.success(
        "Success",
        `Campaign ${campaign ? "updated" : "created"} successfully`
      );
      navigate("..");
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isLoadingProductGroups || isLoadingCountries) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <form css={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
        <div css={bodyStyle}>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                Basic information
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                Name the campaign and choose a country.
              </StyledText>
            </div>
            <FormRow>
              <TextInput
                name="name"
                label="Admin name"
                placeholder="Two sodas for one - April 2025"
                helperText="Only visible for admins"
              />
              <TextInput
                name="title"
                label="Display name"
                helperText="Visible for users"
                placeholder="Two sodas for one"
              />
            </FormRow>
            <FormRow>
              <SelectCountryInput
                name="countryAlpha2Code"
                isDisabled={!!campaign}
                onChange={(value) => {
                  const country = (countries || []).find(
                    (c) => c.countryCode === value
                  );
                  methods.setValue("currency", country?.currency || currency);
                }}
              />
            </FormRow>
            <Disclosure
              renderButton={(open) => (
                <ButtonPrimaryForwardedRef
                  icon={
                    open ? (
                      <FontAwesomeIcon icon={solid("chevron-up")} />
                    ) : (
                      <FontAwesomeIcon icon={solid("chevron-down")} />
                    )
                  }
                >
                  Advanced information
                </ButtonPrimaryForwardedRef>
              )}
            >
              <FormRow className="mt-2">
                <SelectCurrencyInput name="currency" isDisabled={!!campaign} />
              </FormRow>
            </Disclosure>
          </div>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                What should trigger this campaign?
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                The conditions are evaluated based on the user’s cart. Define
                what combination of product groups that should exist in the
                user’s cart for this campaign to be automatically applied to the
                user’s order.
              </StyledText>
            </div>
            {fields.map((field, index) => (
              <React.Fragment key={field.id}>
                {index !== 0 && <SmallBadge>AND</SmallBadge>}
                <TriggerConfigField
                  key={field.id}
                  onRemove={() => {
                    remove(index);
                  }}
                  options={
                    productGroups?.map((pg) => ({
                      label: pg.name,
                      value: pg.id,
                    })) || []
                  }
                  index={index}
                />
              </React.Fragment>
            ))}
            <ErrorMessage
              name="productGroupConfigurations"
              render={({ message }) => (
                <StyledText
                  color={colors.errorDark}
                  className="bodySubtextRegular mb-0"
                >
                  {message}
                </StyledText>
              )}
            />
            <ButtonPrimary
              onClick={() => {
                append({
                  triggerCount: 1,
                  productGroupIds: [],
                });
              }}
              icon={<FontAwesomeIcon icon={solid("plus")} />}
            >
              Add condition
            </ButtonPrimary>
          </div>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                What does the user get when this campaign is applied?
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                Choose between a percentage discount on the products or a fixed
                price for the products. OBS: The discount is only applied to the
                applicable products. E.g. a percentage discount of 10% is not
                applied to the complete order, but only on the above products.
                The same for fixed price.
              </StyledText>
            </div>
            <div css={discountTypeWrapperStyle}>
              <LabelledRadioGroupInput
                name="discountType"
                options={[
                  {
                    label: "Percentage discount on products",
                    value: "percentage_discount",
                    description: "Buy above products with a % discount",
                  },
                  {
                    label: "Fixed price",
                    value: "fixed_price",
                    description: "Buy above products for a fixed price",
                  },
                ]}
              />
            </div>
            {methods.watch("discountType") === "percentage_discount" ? (
              <TextInput
                name="discountValue"
                type="number"
                label="Discount percentage (%)"
              />
            ) : (
              <PriceInput
                name="discountValue"
                label="Discount value"
                currencyName="currency"
                formMethods={methods}
              />
            )}
          </div>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                When should this campaign be active?
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                You can choose a to and from date that defines when the campaign
                is active. Leave both blank if you want the campaign to run
                forever. Leave from date blank if this campaign should be active
                immediately and leave to date blank if the campaign should never
                expire.
              </StyledText>
            </div>
            <FormRow>
              <DatePickerInput
                name="activeFrom"
                label="From"
                placeholder="07 May, 2024"
                maxDate={
                  methods.watch("activeTo")
                    ? new Date(methods.watch("activeTo"))
                    : undefined
                }
              />
              <DatePickerInput
                name="activeTo"
                minDate={
                  methods.watch("activeFrom")
                    ? new Date(methods.watch("activeFrom"))
                    : undefined
                }
                label="To"
              />
            </FormRow>
          </div>
          <div css={bodySectionStyle}>
            <div css={bodyHeaderStyle}>
              <StyledText className="bodyTextSemibold mb-0">
                Enable/disable campaign
              </StyledText>
              <StyledText className="bodySubtextRegular mb-0">
                You can disable the campaign if you want to stop it from working
                below.
              </StyledText>
            </div>
            <SwitchInput
              name="enabled"
              label="Campaign is enabled and available for users"
            />
          </div>
        </div>
        <div css={buttonWrapperStyle}>
          <ButtonInternalLink to={".."}>Cancel</ButtonInternalLink>
          <ButtonAction type="submit" disabled={methods.formState.isSubmitting}>
            {methods.formState.isSubmitting ? (
              <ActionSpinner />
            ) : campaign ? (
              "Update campaign"
            ) : (
              "Create campaign"
            )}
          </ButtonAction>
        </div>
      </form>
    </FormProvider>
  );
};
